<template>
  <div class="trn-player">

    <!-- Loading -->
    <div v-if="loading" class="player-loading">
      <div class="spinner"></div>
      <p>Loading course…</p>
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="player-error">
      <font-awesome-icon icon="exclamation-circle" class="err-icon" />
      <p>{{ loadError }}</p>
      <button class="btn-outline" @click="$router.go(-1)">Go Back</button>
    </div>

    <!-- Player -->
    <template v-else>

      <!-- Header -->
      <header class="player-header">
        <button class="header-back" @click="$router.go(-1)">
          <font-awesome-icon icon="arrow-left" /> My Training
        </button>
        <div class="header-center">
          <span class="header-course-title">{{ enrolment.course.title }}</span>
          <span v-if="enrolment.status === 'completed'" class="status-pill status-pill--done">Completed</span>
          <span v-else-if="enrolment.status === 'overdue'" class="status-pill status-pill--overdue">Overdue</span>
        </div>
        <div class="header-progress">
          <div class="prog-bar"><div class="prog-fill" :style="{ width: enrolment.progress_pct + '%' }"></div></div>
          <span class="prog-label">{{ enrolment.progress_pct || 0 }}%</span>
        </div>
      </header>

      <!-- Prerequisites warning -->
      <div v-if="!prerequisitesMet" class="prereq-banner">
        <font-awesome-icon icon="lock" />
        Some prerequisites for this course have not been completed. You can still view content but some features may be restricted.
      </div>

      <!-- Body -->
      <div class="player-body">

        <!-- Sidebar -->
        <nav class="player-nav" :class="{ 'player-nav--open': navOpen }">
          <button class="nav-close-btn" @click="navOpen = false"><font-awesome-icon icon="times" /></button>

          <div v-if="!allLessons.length" class="nav-empty">No lessons in this course yet.</div>

          <template v-for="section in sections" :key="section.id">
            <div class="nav-section-title">{{ section.title }}</div>
            <button
              v-for="lesson in section.lessons"
              :key="lesson.id"
              :class="['nav-lesson', { 'nav-lesson--active': currentLesson?.id === lesson.id, 'nav-lesson--locked': isLessonLocked(lesson) }]"
              @click="selectLesson(lesson)"
            >
              <span class="nav-status">
                <font-awesome-icon v-if="isLessonLocked(lesson)" icon="lock" class="ic-locked" />
                <font-awesome-icon v-else-if="lessonStatus(lesson.id) === 'completed'" icon="check-circle" class="ic-done" />
                <font-awesome-icon v-else-if="lessonStatus(lesson.id) === 'in_progress'" icon="hourglass-half" class="ic-progress" />
                <font-awesome-icon v-else icon="circle" class="ic-todo" />
              </span>
              <span class="nav-lesson-title">{{ lesson.title }}</span>
              <span class="nav-type">{{ typeShort(lesson.lesson_type) }}</span>
            </button>
          </template>

          <!-- Flat lessons (no section) -->
          <template v-if="flatLessons.length">
            <div v-if="sections.length" class="nav-section-title">Other Lessons</div>
            <button
              v-for="lesson in flatLessons"
              :key="lesson.id"
              :class="['nav-lesson', { 'nav-lesson--active': currentLesson?.id === lesson.id, 'nav-lesson--locked': isLessonLocked(lesson) }]"
              @click="selectLesson(lesson)"
            >
              <span class="nav-status">
                <font-awesome-icon v-if="isLessonLocked(lesson)" icon="lock" class="ic-locked" />
                <font-awesome-icon v-else-if="lessonStatus(lesson.id) === 'completed'" icon="check-circle" class="ic-done" />
                <font-awesome-icon v-else-if="lessonStatus(lesson.id) === 'in_progress'" icon="hourglass-half" class="ic-progress" />
                <font-awesome-icon v-else icon="circle" class="ic-todo" />
              </span>
              <span class="nav-lesson-title">{{ lesson.title }}</span>
              <span class="nav-type">{{ typeShort(lesson.lesson_type) }}</span>
            </button>
          </template>
        </nav>

        <!-- Content -->
        <main class="player-main">

          <!-- No lesson selected -->
          <div v-if="!currentLesson" class="no-lesson">
            <font-awesome-icon icon="file-alt" class="no-lesson-icon" />
            <p>Select a lesson from the course outline to begin.</p>
            <button class="btn-primary" @click="navOpen = true">View Course Outline</button>
          </div>

          <template v-else>

            <!-- Lesson header -->
            <div class="lesson-head">
              <button class="nav-toggle-btn" @click="navOpen = !navOpen">
                <font-awesome-icon icon="layer-group" /> Outline
              </button>
              <div class="lesson-head-info">
                <h2 class="lesson-title">{{ currentLesson.title }}</h2>
                <span :class="['type-badge', 'type-badge--' + currentLesson.lesson_type]">
                  {{ typeLabel(currentLesson.lesson_type) }}
                </span>
              </div>
            </div>

            <!-- ── VIDEO ──────────────────────────────────────────────── -->
            <div v-if="currentLesson.lesson_type === 'video'" class="content-area">
              <div v-if="videoEmbedUrl" class="video-wrap">
                <iframe
                  :src="videoEmbedUrl"
                  frameborder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                  class="video-iframe"
                ></iframe>
              </div>
              <div v-else-if="currentLesson.external_url" class="link-content">
                <font-awesome-icon icon="file-video" class="content-icon" />
                <p class="content-desc">This lesson contains a video.</p>
                <a :href="currentLesson.external_url" target="_blank" class="btn-open">
                  <font-awesome-icon icon="arrow-right" /> Open Video
                </a>
              </div>
              <div v-else class="link-content">
                <font-awesome-icon icon="file-video" class="content-icon" />
                <p class="content-desc muted">No video URL has been set for this lesson.</p>
              </div>
              <div class="mark-wrap">
                <button
                  v-if="lessonStatus(currentLesson.id) !== 'completed'"
                  class="btn-mark"
                  @click="markComplete"
                  :disabled="completing"
                >
                  <span v-if="completing" class="spinner-sm"></span>
                  <font-awesome-icon v-else icon="check" />
                  Mark as Watched
                </button>
                <span v-else class="done-label"><font-awesome-icon icon="check-circle" /> Completed</span>
              </div>
            </div>

            <!-- ── AUDIO ──────────────────────────────────────────────── -->
            <div v-else-if="currentLesson.lesson_type === 'audio'" class="content-area">
              <div v-if="currentLesson.external_url" class="audio-wrap">
                <audio controls :src="currentLesson.external_url" class="audio-player"></audio>
              </div>
              <div v-else class="link-content">
                <p class="muted">No audio URL configured.</p>
              </div>
              <div class="mark-wrap">
                <button
                  v-if="lessonStatus(currentLesson.id) !== 'completed'"
                  class="btn-mark"
                  @click="markComplete"
                  :disabled="completing"
                >
                  <span v-if="completing" class="spinner-sm"></span>
                  <font-awesome-icon v-else icon="check" />
                  Mark as Complete
                </button>
                <span v-else class="done-label"><font-awesome-icon icon="check-circle" /> Completed</span>
              </div>
            </div>

            <!-- ── PDF / DOC ───────────────────────────────────────────── -->
            <div v-else-if="['pdf', 'doc'].includes(currentLesson.lesson_type)" class="content-area content-area--doc">
              <div v-if="currentLesson.external_url" class="doc-viewer-wrap">
                <iframe
                  :src="docViewerUrl"
                  class="doc-iframe"
                  frameborder="0"
                  allowfullscreen
                  :key="'doc-' + currentLesson.id"
                ></iframe>
              </div>
              <div v-else class="link-content">
                <font-awesome-icon icon="file-alt" class="content-icon" />
                <p class="content-desc muted">No file URL has been set for this lesson.</p>
              </div>
              <div class="doc-fallback-row">
                <a v-if="currentLesson.external_url" :href="currentLesson.external_url" target="_blank" rel="noopener" class="doc-fallback-link">
                  <font-awesome-icon icon="external-link-alt" /> Trouble viewing? Open in new tab
                </a>
              </div>
              <div class="mark-wrap">
                <button
                  v-if="lessonStatus(currentLesson.id) !== 'completed'"
                  class="btn-mark"
                  @click="markComplete"
                  :disabled="completing"
                >
                  <span v-if="completing" class="spinner-sm"></span>
                  <font-awesome-icon v-else icon="check" />
                  Mark as Read
                </button>
                <span v-else class="done-label"><font-awesome-icon icon="check-circle" /> Completed</span>
              </div>
            </div>

            <!-- ── LINK ───────────────────────────────────────────────── -->
            <div v-else-if="currentLesson.lesson_type === 'link'" class="content-area">
              <div class="link-content">
                <font-awesome-icon icon="globe" class="content-icon" />
                <p class="content-desc">Click the link below to open the resource. Your completion will be recorded automatically.</p>
                <a
                  v-if="currentLesson.external_url"
                  :href="currentLesson.external_url"
                  target="_blank"
                  class="btn-open"
                  @click="markComplete"
                >
                  <font-awesome-icon icon="arrow-right" /> Open Link
                </a>
              </div>
              <div class="mark-wrap">
                <span v-if="lessonStatus(currentLesson.id) === 'completed'" class="done-label">
                  <font-awesome-icon icon="check-circle" /> Completed
                </span>
              </div>
            </div>

            <!-- ── TEXT ───────────────────────────────────────────────── -->
            <div v-else-if="currentLesson.lesson_type === 'text'" class="content-area">
              <div class="text-content" v-html="currentLesson.rich_content || ('<p>' + currentLesson.title + '</p>')"></div>
              <div class="mark-wrap">
                <button
                  v-if="lessonStatus(currentLesson.id) !== 'completed'"
                  class="btn-mark"
                  @click="markComplete"
                  :disabled="completing"
                >
                  <span v-if="completing" class="spinner-sm"></span>
                  <font-awesome-icon v-else icon="check" />
                  Mark as Read
                </button>
                <span v-else class="done-label"><font-awesome-icon icon="check-circle" /> Completed</span>
              </div>
            </div>

            <!-- ── ACK ────────────────────────────────────────────────── -->
            <div v-else-if="currentLesson.lesson_type === 'ack'" class="content-area">
              <div class="ack-content">
                <font-awesome-icon icon="check-double" class="content-icon content-icon--ack" />
                <h3 class="ack-title">Acknowledgement Required</h3>
                <p class="ack-desc">{{ currentLesson.rich_content || 'Please read and acknowledge this item to continue.' }}</p>
                <button
                  v-if="lessonStatus(currentLesson.id) !== 'completed'"
                  class="btn-ack"
                  @click="markComplete"
                  :disabled="completing"
                >
                  <span v-if="completing" class="spinner-sm"></span>
                  <font-awesome-icon v-else icon="check" />
                  I Acknowledge
                </button>
                <span v-else class="done-label"><font-awesome-icon icon="check-circle" /> Acknowledged</span>
              </div>
            </div>

            <!-- ── QUIZ ───────────────────────────────────────────────── -->
            <div v-else-if="currentLesson.lesson_type === 'quiz'" class="content-area">

              <!-- Not started -->
              <div v-if="!quiz.started && !quiz.result" class="quiz-start">
                <font-awesome-icon icon="tasks" class="content-icon content-icon--quiz" />
                <h3 class="quiz-title">Quiz</h3>
                <p class="quiz-desc" v-if="currentLesson.quizConfig">
                  {{ currentLesson.quizConfig.questions_per_attempt }} question(s) ·
                  Pass mark: {{ enrolment.course.pass_mark_pct }}%
                  <template v-if="currentLesson.quizConfig.time_limit_min">
                    · {{ currentLesson.quizConfig.time_limit_min }} min time limit
                  </template>
                </p>
                <button class="btn-mark" @click="startQuiz" :disabled="quiz.loading">
                  <span v-if="quiz.loading" class="spinner-sm"></span>
                  <font-awesome-icon v-else icon="check-circle" />
                  Start Quiz
                </button>
                <p v-if="quiz.error" class="quiz-error">{{ quiz.error }}</p>
              </div>

              <!-- Questions -->
              <div v-else-if="quiz.started && !quiz.result" class="quiz-questions">
                <div class="quiz-progress-bar">
                  <span>{{ answeredCount }} / {{ quiz.questions.length }} answered</span>
                </div>
                <div v-for="(q, qi) in quiz.questions" :key="q.id" class="quiz-question">
                  <p class="q-text"><strong>{{ qi + 1 }}.</strong> {{ q.question_text }}</p>

                  <!-- Single choice / true_false -->
                  <div v-if="['single_choice', 'true_false'].includes(q.question_type)" class="q-options">
                    <label v-for="opt in q.options" :key="opt.id" class="q-option">
                      <input type="radio" :name="'q-' + q.id" :value="opt.id" v-model="quiz.answers[q.id]" class="q-radio" />
                      <span>{{ opt.text }}</span>
                    </label>
                  </div>

                  <!-- Multiple choice -->
                  <div v-else-if="q.question_type === 'multiple_choice'" class="q-options">
                    <label v-for="opt in q.options" :key="opt.id" class="q-option">
                      <input type="checkbox" :value="opt.id" v-model="quiz.multiAnswers[q.id]" class="q-check" />
                      <span>{{ opt.text }}</span>
                    </label>
                  </div>

                  <!-- Short answer / essay -->
                  <div v-else class="q-text-answer">
                    <textarea
                      v-model="quiz.answers[q.id]"
                      class="q-textarea"
                      :rows="q.question_type === 'essay' ? 5 : 2"
                      placeholder="Your answer…"
                    ></textarea>
                  </div>
                </div>

                <div class="quiz-submit-row">
                  <button class="btn-mark" @click="submitQuiz" :disabled="quiz.submitting">
                    <span v-if="quiz.submitting" class="spinner-sm"></span>
                    <font-awesome-icon v-else icon="check" />
                    Submit Quiz
                  </button>
                  <p v-if="quiz.error" class="quiz-error">{{ quiz.error }}</p>
                </div>
              </div>

              <!-- Result -->
              <div v-else-if="quiz.result" class="quiz-result">
                <div :class="['result-icon-wrap', quiz.result.passed ? 'result-pass' : 'result-fail']">
                  <font-awesome-icon :icon="quiz.result.passed ? 'trophy' : 'times'" />
                </div>
                <h3 class="result-title">{{ quiz.result.passed ? 'Quiz Passed!' : 'Not Passed' }}</h3>
                <p v-if="quiz.result.score_pct !== null" class="result-score">
                  Score: <strong>{{ quiz.result.score_pct }}%</strong>
                </p>
                <p v-else class="result-score muted">Awaiting manual grading.</p>
                <button v-if="!quiz.result.passed" class="btn-outline" @click="retryQuiz">Try Again</button>
              </div>

            </div>

            <!-- ── OTHER ──────────────────────────────────────────────── -->
            <div v-else class="content-area">
              <div class="link-content">
                <p class="muted">Content type "{{ currentLesson.lesson_type }}" is not yet supported in the player.</p>
              </div>
            </div>

            <!-- Navigation -->
            <div class="lesson-nav">
              <button class="btn-nav" :disabled="lessonIndex <= 0" @click="goLesson(-1)">
                <font-awesome-icon icon="arrow-left" /> Previous
              </button>
              <span v-if="isSequential && !canAdvance && lessonIndex < allLessons.length - 1" class="seq-lock-note">
                <font-awesome-icon icon="lock" /> Complete this lesson to continue
              </span>
              <button class="btn-nav btn-nav--next" :disabled="lessonIndex >= allLessons.length - 1 || !canAdvance" @click="goLesson(1)">
                Next <font-awesome-icon icon="arrow-right" />
              </button>
            </div>

          </template>
        </main>
      </div>

      <!-- Completion overlay -->
      <div v-if="showCompletionBanner" class="completion-banner">
        <div class="completion-content">
          <font-awesome-icon icon="trophy" class="trophy-icon" />
          <h3>Course Complete!</h3>
          <p>You've completed <strong>{{ enrolment.course.title }}</strong>.</p>
          <div class="completion-actions">
            <button class="btn-primary" @click="$router.go(-1)">Back to My Training</button>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'TrainingPlayer',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: true,
      loadError: null,
      enrolment: null,
      sections: [],
      flatLessons: [],
      progressMap: {},      // { lessonId: { status, progress_pct } }
      prerequisitesMet: true,

      currentLesson: null,
      completing: false,
      navOpen: false,
      showCompletionBanner: false,

      quiz: {
        loading: false,
        started: false,
        attemptId: null,
        questions: [],
        answers: {},       // { questionId: selectedOptionId or text }
        multiAnswers: {},  // { questionId: [optionId, ...] }
        submitting: false,
        result: null,
        error: null,
      },
    };
  },
  computed: {
    enrolmentId() { return this.$route.params.enrolmentId; },

    allLessons() {
      return [
        ...this.sections.flatMap(s => s.lessons || []),
        ...this.flatLessons,
      ];
    },

    lessonIndex() {
      return this.allLessons.findIndex(l => l.id === this.currentLesson?.id);
    },

    isSequential() {
      return !!this.enrolment?.course?.sequential;
    },

    canAdvance() {
      if (!this.isSequential) return true;
      return this.lessonStatus(this.currentLesson?.id) === 'completed';
    },

    videoEmbedUrl() {
      const url = this.currentLesson?.external_url;
      if (!url) return null;
      const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0`;
      const vimeo = url.match(/vimeo\.com\/(\d+)/);
      if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
      return null;
    },

    docViewerUrl() {
      const url = this.currentLesson?.external_url;
      if (!url) return null;
      const type = this.currentLesson.lesson_type;

      // Google Drive: /view or /edit → /preview (works for both PDF and Docs)
      const gDrive = url.match(/drive\.google\.com\/file\/d\/([^/?#]+)/);
      if (gDrive) return `https://drive.google.com/file/d/${gDrive[1]}/preview`;

      // Dropbox: swap to direct content host and strip dl param
      if (url.includes('dropbox.com')) {
        return url
          .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
          .replace(/[?&]dl=\d/, '');
      }

      // OneDrive/SharePoint: wrap in Office Online embed
      if (url.includes('onedrive.live.com') || url.includes('1drv.ms') || url.includes('sharepoint.com')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }

      // Office file extensions: use Office Online viewer (better than Google Docs viewer)
      if (/\.(docx?|pptx?|xlsx?)([?#]|$)/i.test(url)) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }

      // Generic doc: try Google Docs viewer
      if (type === 'doc') {
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      }

      // PDF: return direct URL (browser renders natively in iframe)
      return url;
    },

    answeredCount() {
      return this.quiz.questions.filter(q => {
        if (q.question_type === 'multiple_choice') return (this.quiz.multiAnswers[q.id] || []).length > 0;
        return !!this.quiz.answers[q.id];
      }).length;
    },
  },
  async created() {
    await this.loadEnrolment();
  },
  methods: {

    async loadEnrolment() {
      this.loading = true;
      this.loadError = null;
      try {
        const { data } = await axios.get(`/training/me/enrolments/${this.enrolmentId}`);
        const payload = data.data;
        this.enrolment       = payload.enrolment;
        this.prerequisitesMet = payload.prerequisites_met ?? true;

        const course = this.enrolment.course;
        this.sections    = (course.sections || []).map(s => ({ ...s, lessons: s.lessons || [] }));
        this.flatLessons = course.flat_lessons || [];

        // Build progress map
        this.progressMap = {};
        for (const p of this.enrolment.lesson_progress ?? this.enrolment.lessonProgress ?? []) {
          this.progressMap[p.lesson_id] = { status: p.status, progress_pct: p.progress_pct };
        }

        // Auto-select first incomplete lesson
        const first = this.allLessons.find(l => this.lessonStatus(l.id) !== 'completed') || this.allLessons[0];
        if (first) this.currentLesson = first;
      } catch (e) {
        this.loadError = e.response?.data?.message ?? 'Failed to load course. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    lessonStatus(lessonId) {
      return this.progressMap[lessonId]?.status ?? 'not_started';
    },

    isLessonLocked(lesson) {
      if (!this.isSequential) return false;
      const idx = this.allLessons.findIndex(l => l.id === lesson.id);
      if (idx <= 0) return false;
      return this.allLessons.slice(0, idx).some(l => this.lessonStatus(l.id) !== 'completed');
    },

    selectLesson(lesson) {
      if (this.isLessonLocked(lesson)) {
        this.toast.info('Complete the previous lessons to unlock this one.');
        return;
      }
      this.currentLesson = lesson;
      this.resetQuiz();
      this.navOpen = false;
    },

    goLesson(dir) {
      if (dir > 0 && !this.canAdvance) return;
      const idx = this.lessonIndex + dir;
      if (idx < 0 || idx >= this.allLessons.length) return;
      this.selectLesson(this.allLessons[idx]);
    },

    typeLabel(type) {
      return {
        video: 'Video', audio: 'Audio', pdf: 'PDF', doc: 'Document',
        link: 'Link', text: 'Article', ack: 'Acknowledgement',
        quiz: 'Quiz', scorm: 'SCORM', xapi: 'xAPI', classroom: 'Classroom',
      }[type] || type;
    },

    typeShort(type) {
      return {
        video: 'VID', audio: 'AUD', pdf: 'PDF', doc: 'DOC',
        link: 'URL', text: 'TXT', ack: 'ACK', quiz: 'QUIZ',
      }[type] || type.toUpperCase().slice(0, 4);
    },

    // ── Mark complete ─────────────────────────────────────────────────────

    async markComplete() {
      if (this.completing) return;
      const lesson = this.currentLesson;
      if (!lesson) return;

      this.completing = true;
      try {
        const base = `/training/me/enrolments/${this.enrolmentId}/lessons/${lesson.id}`;

        if (['ack', 'link'].includes(lesson.lesson_type)) {
          await axios.post(`${base}/acknowledge`);
        } else if (['video', 'audio'].includes(lesson.lesson_type)) {
          await this.sendVideoHeartbeats(base, lesson);
        } else {
          await axios.post(`${base}/progress`, {
            type:            lesson.lesson_type,
            elapsed_seconds: 30,
            pages_viewed:    [1],
          });
        }

        // Update local progress
        this.progressMap[lesson.id] = { status: 'completed', progress_pct: 100 };

        // Refresh enrolment progress_pct
        const { data } = await axios.get(`/training/me/enrolments/${this.enrolmentId}`);
        const updated = data.data?.enrolment;
        if (updated) {
          this.enrolment.progress_pct = updated.progress_pct;
          this.enrolment.status       = updated.status;
        }

        // Check if all done
        const allDone = this.allLessons.every(l => this.lessonStatus(l.id) === 'completed');
        if (allDone || this.enrolment.status === 'completed') {
          this.showCompletionBanner = true;
          return;
        }

        // Auto-advance to next lesson
        const next = this.allLessons.find((l, i) => i > this.lessonIndex && this.lessonStatus(l.id) !== 'completed');
        if (next) this.selectLesson(next);

        this.toast.success('Lesson marked as complete.');
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Could not mark lesson complete.');
      } finally {
        this.completing = false;
      }
    },

    async sendVideoHeartbeats(base, lesson) {
      const duration = lesson.duration_seconds || 60;
      const chunkSize = 300;
      for (let start = 0; start < duration; start += chunkSize) {
        const end     = Math.min(start + chunkSize, duration);
        const elapsed = end - start;
        await axios.post(`${base}/progress`, {
          type:            lesson.lesson_type,
          elapsed_seconds: elapsed,
          start,
          end,
          position:        end,
        });
      }
    },

    // ── Quiz ──────────────────────────────────────────────────────────────

    resetQuiz() {
      this.quiz = {
        loading: false, started: false, attemptId: null,
        questions: [], answers: {}, multiAnswers: {},
        submitting: false, result: null, error: null,
      };
    },

    async startQuiz() {
      this.quiz.loading = true;
      this.quiz.error   = null;
      try {
        const { data } = await axios.post(
          `/training/me/enrolments/${this.enrolmentId}/lessons/${this.currentLesson.id}/quiz/start`
        );
        const payload = data.data;
        this.quiz.attemptId = payload.attempt_id;
        this.quiz.questions = payload.questions || [];
        this.quiz.answers      = {};
        this.quiz.multiAnswers = {};
        this.quiz.questions.forEach(q => {
          if (q.question_type === 'multiple_choice') this.quiz.multiAnswers[q.id] = [];
          else this.quiz.answers[q.id] = '';
        });
        this.quiz.started = true;
      } catch (e) {
        this.quiz.error = e.response?.data?.message ?? 'Failed to start quiz.';
      } finally {
        this.quiz.loading = false;
      }
    },

    async submitQuiz() {
      this.quiz.submitting = true;
      this.quiz.error      = null;
      try {
        const answers = this.quiz.questions.map(q => {
          let response = null;
          if (q.question_type === 'multiple_choice') {
            response = JSON.stringify(this.quiz.multiAnswers[q.id] || []);
          } else {
            response = this.quiz.answers[q.id] || null;
          }
          return { question_id: q.id, response };
        });

        const { data } = await axios.post(
          `/training/me/enrolments/${this.enrolmentId}/attempts/${this.quiz.attemptId}/submit`,
          { answers }
        );
        const result = data.data;
        this.quiz.result  = result;
        this.quiz.started = false;

        // Update progress if passed
        if (result.passed) {
          this.progressMap[this.currentLesson.id] = { status: 'completed', progress_pct: 100 };
          this.enrolment.progress_pct = result.course_pct ?? this.enrolment.progress_pct;

          const allDone = this.allLessons.every(l => this.lessonStatus(l.id) === 'completed');
          if (allDone || this.enrolment.status === 'completed') {
            setTimeout(() => { this.showCompletionBanner = true; }, 1500);
          }
        }
      } catch (e) {
        this.quiz.error = e.response?.data?.message ?? 'Failed to submit quiz.';
      } finally {
        this.quiz.submitting = false;
      }
    },

    retryQuiz() {
      this.quiz.result  = null;
      this.quiz.started = false;
      this.quiz.error   = null;
    },
  },
};
</script>

<style scoped>
/* ── Base ───────────────────────────────────────────────────────────────── */
.trn-player { display:flex; flex-direction:column; height:100vh; background:#f4f7fb; overflow:hidden; }

/* ── Loading / Error ────────────────────────────────────────────────────── */
.player-loading { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; color:#7a90b0; }
.player-error   { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; color:#e03535; text-align:center; padding:24px; }
.err-icon { font-size:40px; opacity:.6; }

/* ── Header ─────────────────────────────────────────────────────────────── */
.player-header { display:flex; align-items:center; gap:16px; background:#fff; border-bottom:1px solid #e4eaf2; padding:12px 20px; flex-shrink:0; position:sticky; top:0; z-index:10; }
.header-back { display:inline-flex; align-items:center; gap:7px; background:none; border:none; color:#5a7aaa; font-size:13px; font-weight:600; cursor:pointer; padding:6px 10px; border-radius:8px; flex-shrink:0; }
.header-back:hover { background:#f0f4f8; color:#1a2b45; }
.header-center { flex:1; min-width:0; display:flex; align-items:center; gap:10px; }
.header-course-title { font-size:14px; font-weight:700; color:#1a2b45; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.status-pill { padding:2px 8px; border-radius:10px; font-size:11px; font-weight:700; flex-shrink:0; }
.status-pill--done    { background:#e8f5e9; color:#2e7d32; }
.status-pill--overdue { background:#fff0f0; color:#c03030; }
.header-progress { display:flex; align-items:center; gap:8px; flex-shrink:0; }
.prog-bar { width:100px; height:6px; background:#e4eaf2; border-radius:3px; overflow:hidden; }
.prog-fill { height:100%; background:#004AAD; border-radius:3px; transition:width .3s; }
.prog-label { font-size:12px; font-weight:700; color:#004AAD; }

/* ── Prerequisites banner ────────────────────────────────────────────────── */
.prereq-banner { background:#fff8e6; border-bottom:1px solid #f0d080; padding:10px 20px; font-size:13px; color:#8a6000; display:flex; align-items:center; gap:10px; flex-shrink:0; }

/* ── Body layout ────────────────────────────────────────────────────────── */
.player-body { display:flex; flex:1; overflow:hidden; }

/* ── Sidebar nav ────────────────────────────────────────────────────────── */
.player-nav { width:280px; flex-shrink:0; background:#fff; border-right:1px solid #e4eaf2; overflow-y:auto; display:flex; flex-direction:column; padding:12px 0; }
.nav-close-btn { display:none; }
.nav-section-title { font-size:10px; font-weight:800; color:#a0b0c8; text-transform:uppercase; letter-spacing:.07em; padding:12px 16px 4px; }
.nav-empty { padding:20px 16px; font-size:13px; color:#a0b0c8; text-align:center; }

.nav-lesson { display:flex; align-items:center; gap:8px; width:100%; background:none; border:none; text-align:left; padding:9px 16px; cursor:pointer; transition:background .1s; }
.nav-lesson:hover { background:#f4f7fb; }
.nav-lesson--active { background:#eef4ff; }
.nav-lesson--locked { opacity:.55; cursor:not-allowed; }
.nav-lesson--locked:hover { background:transparent; }
.nav-status { width:18px; flex-shrink:0; font-size:13px; text-align:center; }
.ic-done      { color:#2d8a3e; }
.ic-progress  { color:#e07000; }
.ic-todo      { color:#c8d4e8; font-size:10px; }
.ic-locked    { color:#b0bdd0; font-size:11px; }
.nav-lesson-title { flex:1; font-size:13px; color:#1a2b45; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.nav-lesson--active .nav-lesson-title { color:#004AAD; font-weight:600; }
.nav-type { font-size:10px; font-weight:700; color:#a0b0c8; background:#f0f4f8; padding:2px 5px; border-radius:4px; flex-shrink:0; }

/* ── Main content ───────────────────────────────────────────────────────── */
.player-main { flex:1; overflow-y:auto; display:flex; flex-direction:column; }

.no-lesson { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; color:#a0b0c8; padding:40px; text-align:center; }
.no-lesson-icon { font-size:48px; opacity:.4; }

.lesson-head { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 0; }
.nav-toggle-btn { display:none; }
.lesson-head-info { flex:1; min-width:0; }
.lesson-title { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.type-badge { display:inline-block; padding:3px 10px; border-radius:6px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; background:#eef2ff; color:#4060c8; }
.type-badge--video  { background:#e8f4ff; color:#0060b0; }
.type-badge--audio  { background:#f0f8ee; color:#2a7a3a; }
.type-badge--pdf    { background:#fff0f0; color:#c03030; }
.type-badge--doc    { background:#edf7f5; color:#1a7a6a; }
.type-badge--link   { background:#fff8e8; color:#9a6500; }
.type-badge--text   { background:#f5f0ff; color:#6040c0; }
.type-badge--ack    { background:#f0f8ee; color:#2a7a3a; }
.type-badge--quiz   { background:#fff0f8; color:#b03080; }

/* ── Content area ───────────────────────────────────────────────────────── */
.content-area { padding:20px 24px; flex:1; }
.content-area--doc { padding:16px 24px 0; display:flex; flex-direction:column; }

.doc-viewer-wrap { flex:1; }
.doc-iframe { width:100%; height:calc(100vh - 280px); min-height:420px; border:none; border-radius:10px; background:#f8f8f8; display:block; }

.doc-fallback-row { padding:8px 0 0; }
.doc-fallback-link { font-size:12px; color:#7a90b0; text-decoration:none; display:inline-flex; align-items:center; gap:5px; }
.doc-fallback-link:hover { color:#004AAD; text-decoration:underline; }

.video-wrap { position:relative; padding-bottom:56.25%; height:0; background:#000; border-radius:10px; overflow:hidden; margin-bottom:16px; }
.video-iframe { position:absolute; inset:0; width:100%; height:100%; }

.audio-wrap { padding:20px 0; }
.audio-player { width:100%; border-radius:8px; }

.link-content { display:flex; flex-direction:column; align-items:center; text-align:center; padding:40px 20px; gap:14px; }
.content-icon { font-size:48px; color:#c2d0e8; }
.content-icon--ack  { color:#2a7a3a; }
.content-icon--quiz { color:#b03080; }
.content-desc { font-size:14px; color:#5a7aaa; max-width:400px; margin:0; }
.btn-open { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; padding:10px 22px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; }
.btn-open:hover { background:#0056d2; }

.text-content { font-size:14px; line-height:1.7; color:#2a3a50; max-width:720px; }

.ack-content { display:flex; flex-direction:column; align-items:center; text-align:center; padding:40px 20px; gap:14px; }
.ack-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.ack-desc  { font-size:14px; color:#5a7aaa; max-width:480px; margin:0; line-height:1.6; }
.btn-ack { display:inline-flex; align-items:center; gap:8px; background:#2d8a3e; color:#fff; padding:11px 24px; border-radius:8px; font-size:14px; font-weight:600; border:none; cursor:pointer; }
.btn-ack:hover:not(:disabled) { background:#236b30; }
.btn-ack:disabled { opacity:.6; cursor:not-allowed; }

.mark-wrap { padding:16px 0; display:flex; align-items:center; gap:14px; }
.btn-mark { display:inline-flex; align-items:center; gap:8px; background:#004AAD; color:#fff; padding:10px 22px; border-radius:8px; font-size:13px; font-weight:600; border:none; cursor:pointer; }
.btn-mark:hover:not(:disabled) { background:#0056d2; }
.btn-mark:disabled { opacity:.6; cursor:not-allowed; }
.done-label { display:inline-flex; align-items:center; gap:7px; color:#2d8a3e; font-size:13px; font-weight:600; }

/* ── Quiz ────────────────────────────────────────────────────────────────── */
.quiz-start, .quiz-result { display:flex; flex-direction:column; align-items:center; text-align:center; gap:14px; padding:40px 20px; }
.quiz-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.quiz-desc  { font-size:13px; color:#7a90b0; margin:0; }
.quiz-error { color:#e03535; font-size:13px; }

.quiz-questions { max-width:680px; }
.quiz-progress-bar { padding:12px 0; font-size:12px; color:#7a90b0; border-bottom:1px solid #f0f4f8; margin-bottom:16px; }
.quiz-question { margin-bottom:24px; padding-bottom:24px; border-bottom:1px solid #f0f4f8; }
.quiz-question:last-of-type { border-bottom:none; }
.q-text { font-size:14px; font-weight:600; color:#1a2b45; margin:0 0 10px; line-height:1.5; }
.q-options { display:flex; flex-direction:column; gap:8px; }
.q-option { display:flex; align-items:center; gap:10px; padding:8px 12px; border-radius:8px; cursor:pointer; transition:background .1s; font-size:13px; color:#2a3a50; }
.q-option:hover { background:#f4f7fb; }
.q-radio, .q-check { accent-color:#004AAD; width:15px; height:15px; cursor:pointer; }
.q-textarea { width:100%; border:1.5px solid #dde5f0; border-radius:8px; padding:10px 12px; font-size:13px; color:#1a2b45; resize:vertical; outline:none; }
.q-textarea:focus { border-color:#004AAD; }
.q-text-answer { margin-top:8px; }
.quiz-submit-row { padding:16px 0; }

.result-icon-wrap { width:64px; height:64px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:28px; }
.result-pass { background:#e8f5e9; color:#2d8a3e; }
.result-fail { background:#fff0f0; color:#c03030; }
.result-title { font-size:20px; font-weight:700; color:#1a2b45; margin:0; }
.result-score { font-size:15px; color:#5a7aaa; margin:0; }

/* ── Lesson navigation ───────────────────────────────────────────────────── */
.lesson-nav { display:flex; align-items:center; justify-content:space-between; padding:16px 24px; border-top:1px solid #f0f4f8; background:#fff; flex-shrink:0; position:sticky; bottom:0; }
.seq-lock-note { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#a0b0c8; font-weight:500; }
.btn-nav { display:inline-flex; align-items:center; gap:7px; background:#fff; border:1.5px solid #dde5f0; color:#1a2b45; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-nav:hover:not(:disabled) { background:#f4f7fb; }
.btn-nav:disabled { opacity:.4; cursor:not-allowed; }
.btn-nav--next { background:#004AAD; color:#fff; border-color:#004AAD; }
.btn-nav--next:hover:not(:disabled) { background:#0056d2; }

/* ── Completion overlay ──────────────────────────────────────────────────── */
.completion-banner { position:fixed; inset:0; background:rgba(10,20,40,.7); display:flex; align-items:center; justify-content:center; z-index:100; }
.completion-content { background:#fff; border-radius:20px; padding:40px; text-align:center; max-width:400px; width:90%; box-shadow:0 20px 60px rgba(0,0,0,.25); }
.trophy-icon { font-size:56px; color:#e07000; margin-bottom:16px; display:block; }
.completion-content h3 { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 8px; }
.completion-content p  { font-size:14px; color:#7a90b0; margin:0 0 24px; }
.completion-actions { display:flex; gap:10px; justify-content:center; }

/* ── Shared ─────────────────────────────────────────────────────────────── */
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:500; padding:10px 22px; border-radius:8px; cursor:pointer; }
.btn-primary:hover { background:#0056d2; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.muted { color:#a0b0c8; }

.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media(max-width:768px) {
  .player-nav {
    position:fixed; left:-100%; top:0; bottom:0; width:280px; z-index:50;
    box-shadow:4px 0 20px rgba(0,0,0,.15); transition:left .25s;
  }
  .player-nav--open { left:0; }
  .nav-close-btn {
    display:flex; align-items:center; justify-content:flex-end; padding:10px 14px;
    border:none; background:none; color:#a0b0c8; font-size:18px; cursor:pointer; align-self:flex-end;
  }
  .nav-toggle-btn {
    display:inline-flex; align-items:center; gap:6px; background:#f0f4f8; border:none;
    color:#5a7aaa; font-size:12px; font-weight:600; padding:7px 12px; border-radius:7px; cursor:pointer; flex-shrink:0;
  }
  .header-course-title { font-size:13px; }
  .prog-bar { width:70px; }
  .lesson-head { padding:14px 16px 0; }
  .content-area { padding:14px 16px; }
  .lesson-nav { padding:12px 16px; }
}
</style>
